import { useEffect, useRef, useMemo, useCallback } from 'react';
import { gsap } from 'gsap';
import * as d3 from 'd3';
import { useApp } from '../context/AppContext';
import { formatNaira, formatPercent, formatRank, getPercentChangeColor } from '../utils/formatters';

// Animated counter component
function AnimatedCounter({ value, format }: { value: number; format: (n: number) => string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const prevValue = useRef(0);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const obj = { value: prevValue.current };
    gsap.to(obj, {
      value,
      duration: 1,
      ease: 'power2.out',
      onUpdate: () => {
        if (ref.current) {
          ref.current.textContent = format(obj.value);
        }
      },
    });
    
    prevValue.current = value;
  }, [value, format]);
  
  return <span ref={ref}>{format(value)}</span>;
}

// Mini line chart for state timeline with animated path
function MiniTimeline({ 
  data, 
  selectedYear,
  minYear,
  maxYear,
}: { 
  data: { year: number; total: number }[]; 
  selectedYear: number;
  minYear: number;
  maxYear: number;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const areaRef = useRef<SVGPathElement | null>(null);
  const dotRef = useRef<SVGCircleElement | null>(null);
  const isInitializedRef = useRef(false);
  const prevYearRef = useRef<number>(selectedYear);
  const stateKeyRef = useRef<string>('');
  
  const width = 280;
  const height = 80;
  const margin = { top: 10, right: 10, bottom: 20, left: 10 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  // Create a unique key for the current state data
  const dataKey = useMemo(() => {
    if (data.length === 0) return '';
    return `${data[0].total}-${data[data.length - 1]?.total}`;
  }, [data]);
  
  // Create scales based on FULL data range
  const { xScale, yScale } = useMemo(() => {
    const x = d3
      .scaleLinear()
      .domain([minYear, maxYear])
      .range([0, innerWidth]);
    
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.total) ?? 0])
      .range([innerHeight, 0]);
    
    return { xScale: x, yScale: y };
  }, [data, minYear, maxYear, innerWidth, innerHeight]);
  
  // Line and area generators
  const lineGenerator = useMemo(() => {
    return d3
      .line<{ year: number; total: number }>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d.total))
      .curve(d3.curveMonotoneX);
  }, [xScale, yScale]);
  
  const areaGenerator = useMemo(() => {
    return d3
      .area<{ year: number; total: number }>()
      .x((d) => xScale(d.year))
      .y0(innerHeight)
      .y1((d) => yScale(d.total))
      .curve(d3.curveMonotoneX);
  }, [xScale, yScale, innerHeight]);
  
  // Calculate the path length up to the selected year
  const getPathLengthAtYear = useCallback((year: number): number => {
    if (!pathRef.current) return 0;
    
    const totalLength = pathRef.current.getTotalLength();
    const yearRange = maxYear - minYear;
    if (yearRange === 0) return totalLength;
    
    const progress = (year - minYear) / yearRange;
    return totalLength * progress;
  }, [minYear, maxYear]);
  
  // Initialize or reset chart when state changes
  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;
    
    // Check if we need to reinitialize (new state selected)
    if (stateKeyRef.current !== dataKey) {
      isInitializedRef.current = false;
      stateKeyRef.current = dataKey;
    }
    
    if (isInitializedRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const clipId = `timeline-clip-${Math.random().toString(36).substr(2, 9)}`;
    
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Create clipping path for the area
    svg.append('defs')
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', xScale(selectedYear))
      .attr('height', innerHeight + margin.top);
    
    // Create area path (full path, clipped)
    const areaPath = g.append('path')
      .datum(data)
      .attr('d', areaGenerator)
      .attr('fill', 'rgba(34, 211, 238, 0.1)')
      .attr('stroke', 'none')
      .attr('clip-path', `url(#${clipId})`)
      .attr('class', 'timeline-area');
    
    areaRef.current = areaPath.node();
    
    // Store clip rect reference for animation
    const clipRect = svg.select(`#${clipId} rect`);
    
    // Create line path (full path)
    const linePath = g.append('path')
      .datum(data)
      .attr('d', lineGenerator)
      .attr('fill', 'none')
      .attr('stroke', '#22d3ee')
      .attr('stroke-width', 2)
      .attr('stroke-linecap', 'round')
      .attr('class', 'timeline-line');
    
    pathRef.current = linePath.node();
    
    // Set up the dash pattern for partial reveal
    const totalLength = pathRef.current?.getTotalLength() || 0;
    const visibleLength = getPathLengthAtYear(selectedYear);
    
    linePath
      .attr('stroke-dasharray', totalLength)
      .attr('stroke-dashoffset', totalLength); // Start hidden
    
    // Animate path drawing with elastic effect
    gsap.to(pathRef.current, {
      strokeDashoffset: totalLength - visibleLength,
      duration: 1.2,
      ease: 'elastic.out(1, 0.5)',
      delay: 0.2,
    });
    
    // Animate clip rect for area
    gsap.fromTo(clipRect.node(),
      { attr: { width: 0 } },
      {
        attr: { width: xScale(selectedYear) },
        duration: 1,
        ease: 'power2.out',
        delay: 0.2,
      }
    );
    
    // Create dot
    const currentYearData = data.find((d) => d.year === selectedYear);
    if (currentYearData) {
      const dot = g.append('circle')
        .attr('cx', xScale(currentYearData.year))
        .attr('cy', yScale(currentYearData.total))
        .attr('r', 0)
        .attr('fill', '#22d3ee')
        .attr('stroke', '#0f172a')
        .attr('stroke-width', 2)
        .attr('class', 'timeline-dot');
      
      dotRef.current = dot.node();
      
      // Bounce in the dot
      gsap.to(dotRef.current, {
        attr: { r: 6 },
        duration: 0.6,
        ease: 'elastic.out(1.2, 0.4)',
        delay: 0.8,
      });
    }
    
    // X axis (years) with staggered animation
    const years = data.filter((_, i) => i % 3 === 0).map((d) => d.year);
    g.append('g')
      .attr('transform', `translate(0,${innerHeight + 5})`)
      .selectAll('text')
      .data(years)
      .enter()
      .append('text')
      .attr('x', (d) => xScale(d))
      .attr('y', 10)
      .attr('text-anchor', 'middle')
      .attr('fill', '#64748b')
      .attr('font-size', '9px')
      .attr('opacity', 0)
      .text((d) => d)
      .each(function(_, i) {
        gsap.to(this, {
          opacity: 1,
          duration: 0.3,
          delay: 0.6 + i * 0.05,
          ease: 'power2.out',
        });
      });
    
    isInitializedRef.current = true;
    prevYearRef.current = selectedYear;
  }, [data, dataKey, lineGenerator, areaGenerator, xScale, yScale, innerHeight, margin.left, margin.top, getPathLengthAtYear, selectedYear]);
  
  // Animate when year changes
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (prevYearRef.current === selectedYear) return;
    if (!pathRef.current || !dotRef.current || !svgRef.current) return;
    
    const totalLength = pathRef.current.getTotalLength();
    const targetVisibleLength = getPathLengthAtYear(selectedYear);
    const targetDashOffset = totalLength - targetVisibleLength;
    
    // Animate the line to extend/retract
    gsap.to(pathRef.current, {
      strokeDashoffset: targetDashOffset,
      duration: 0.4,
      ease: 'power2.out',
    });
    
    // Update the clip rect for the area
    const clipRect = d3.select(svgRef.current).select('clipPath rect');
    if (clipRect.node()) {
      gsap.to(clipRect.node(), {
        attr: { width: xScale(selectedYear) },
        duration: 0.4,
        ease: 'power2.out',
      });
    }
    
    // Animate dot to new position
    const currentYearData = data.find((d) => d.year === selectedYear);
    if (currentYearData) {
      gsap.to(dotRef.current, {
        attr: {
          cx: xScale(currentYearData.year),
          cy: yScale(currentYearData.total),
        },
        duration: 0.5,
        ease: 'elastic.out(1, 0.5)',
      });
      
      // Pulse effect
      gsap.fromTo(dotRef.current,
        { attr: { r: 6 } },
        { 
          attr: { r: 8 },
          duration: 0.15,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        }
      );
    }
    
    prevYearRef.current = selectedYear;
  }, [selectedYear, data, xScale, yScale, getPathLengthAtYear]);
  
  return <svg ref={svgRef} width={width} height={height} className="w-full" />;
}

export function StateDetailPanel() {
  const { state, data, clearSelection, currentYearSummary } = useApp();
  const panelRef = useRef<HTMLDivElement>(null);
  
  const selectedState = state.selectedStates[0];
  const isOpen = selectedState && !state.compareMode;
  
  // Get year range
  const { minYear, maxYear } = useMemo(() => {
    if (!data || data.years.length === 0) return { minYear: 2007, maxYear: 2025 };
    return {
      minYear: data.years[0],
      maxYear: data.years[data.years.length - 1],
    };
  }, [data]);
  
  // Get state data
  const stateData = useMemo(() => {
    if (!selectedState || !data) return null;
    
    const summary = data.stateSummaries.get(selectedState);
    if (!summary) return null;
    
    const currentYearData = summary.yearlyData.get(state.selectedYear);
    const ranking = currentYearSummary?.rankings.find((r) => r.state === selectedState);
    
    const timelineData = data.years.map((year) => ({
      year,
      total: summary.yearlyData.get(year)?.total ?? 0,
    }));
    
    // Calculate year-over-year change
    const prevYearData = summary.yearlyData.get(state.selectedYear - 1);
    const yoyChange = prevYearData && currentYearData
      ? ((currentYearData.total - prevYearData.total) / prevYearData.total) * 100
      : 0;
    
    return {
      name: selectedState,
      currentTotal: currentYearData?.total ?? 0,
      rank: ranking?.rank ?? 0,
      percentOfTotal: ranking?.percentOfTotal ?? 0,
      allTimeTotal: summary.allTimeTotal,
      allTimeAverage: summary.allTimeAverage,
      percentChangeFromStart: summary.percentChangeFromStart,
      yoyChange,
      timelineData,
    };
  }, [selectedState, data, state.selectedYear, currentYearSummary]);
  
  // Animate panel entrance
  useEffect(() => {
    if (!panelRef.current) return;
    
    if (isOpen) {
      gsap.fromTo(
        panelRef.current,
        { x: '100%', opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, ease: 'power2.out' }
      );
    } else {
      gsap.to(panelRef.current, {
        x: '100%',
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      });
    }
  }, [isOpen]);
  
  if (!stateData) {
    return null;
  }
  
  return (
    <div
      ref={panelRef}
      className={`
        fixed right-0 top-0 h-full w-full max-w-md z-50
        bg-surface-900/95 backdrop-blur-lg border-l border-surface-700
        transform transition-transform duration-300
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        overflow-y-auto
      `}
    >
      {/* Header */}
      <div className="sticky top-0 bg-surface-900/95 backdrop-blur-sm border-b border-surface-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-display font-bold text-surface-100">
              {stateData.name}
            </h2>
            <p className="text-sm text-surface-400">
              {state.selectedYear} Allocation
            </p>
          </div>
          <button
            onClick={clearSelection}
            className="btn-ghost p-2 rounded-full"
            aria-label="Close panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="p-4 space-y-6">
        {/* Main KPI */}
        <div className="card-hover p-6 text-center">
          <p className="text-sm text-surface-400 mb-2">Total Allocation</p>
          <p className="text-3xl font-display font-bold text-gradient">
            <AnimatedCounter 
              value={stateData.currentTotal} 
              format={(n) => formatNaira(n, true)} 
            />
          </p>
          <div className="flex items-center justify-center gap-4 mt-3">
            <span className="badge-accent">
              {formatRank(stateData.rank)} place
            </span>
            <span className="text-sm text-surface-400">
              {stateData.percentOfTotal.toFixed(1)}% of total
            </span>
          </div>
        </div>
        
        {/* KPI Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Year-over-Year Change */}
          <div className="card p-4">
            <p className="text-xs text-surface-500 mb-1">YoY Change</p>
            <p className={`text-lg font-bold ${getPercentChangeColor(stateData.yoyChange)}`}>
              {formatPercent(stateData.yoyChange)}
            </p>
          </div>
          
          {/* All-time Change */}
          <div className="card p-4">
            <p className="text-xs text-surface-500 mb-1">Since 2007</p>
            <p className={`text-lg font-bold ${getPercentChangeColor(stateData.percentChangeFromStart)}`}>
              {formatPercent(stateData.percentChangeFromStart)}
            </p>
          </div>
          
          {/* All-time Total */}
          <div className="card p-4">
            <p className="text-xs text-surface-500 mb-1">All-time Total</p>
            <p className="text-lg font-bold text-surface-100">
              {formatNaira(stateData.allTimeTotal, true)}
            </p>
          </div>
          
          {/* Average per Year */}
          <div className="card p-4">
            <p className="text-xs text-surface-500 mb-1">Avg/Year</p>
            <p className="text-lg font-bold text-surface-100">
              {formatNaira(stateData.allTimeAverage * 12, true)}
            </p>
          </div>
        </div>
        
        {/* Timeline Chart */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-surface-300 mb-3">
            Allocation Timeline ({minYear}-{maxYear})
          </h3>
          <MiniTimeline 
            data={stateData.timelineData} 
            selectedYear={state.selectedYear}
            minYear={minYear}
            maxYear={maxYear}
          />
          <p className="text-xs text-surface-500 mt-2 text-center">
            Showing data up to {state.selectedYear}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={clearSelection}
            className="btn-secondary flex-1"
          >
            Compare with others
          </button>
        </div>
      </div>
    </div>
  );
}
